import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Loading.css';

const Loading = ({ 
  message = "INITIALIZING SYSTEM...", 
  subMessage = "CYPHER.NET", 
  showProgress = true,
  variant = "default" 
}) => {
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(0);
  
  const loadingMessages = [
    "INITIALIZING NEURAL NETWORKS...",
    "LOADING AUDIO SYSTEMS...",
    "CONNECTING TO CYPHER.NET...",
    "CALIBRATING INTERFACES...",
    "SYSTEM READY"
  ];

  useEffect(() => {
    if (showProgress) {
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + Math.random() * 3;
        });
      }, 100);

      return () => clearInterval(progressInterval);
    }
  }, [showProgress]);

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % loadingMessages.length);
    }, 1500);

    return () => clearInterval(messageInterval);
  }, []);

  if (variant === "minimal") {
    return (
      <div className="loading loading--minimal">
        <div className="loading__spinner-minimal">
          <div className="loading__spinner-ring"></div>
          <div className="loading__spinner-ring"></div>
          <div className="loading__spinner-ring"></div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="loading"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Background Grid */}
      <div className="loading__background">
        <div className="loading__grid"></div>
        <div className="loading__particles">
          {Array.from({ length: 20 }).map((_, i) => (
            <div 
              key={i} 
              className="loading__particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
      </div>

      <div className="loading__container">
        {/* Main Loading Animation */}
        <div className="loading__main">
          <div className="loading__spinner-container">
            <div className="loading__spinner">
              <div className="loading__spinner-outer">
                <div className="loading__spinner-inner"></div>
              </div>
              <div className="loading__spinner-center">
                <div className="loading__spinner-dot"></div>
              </div>
            </div>
          </div>

          {/* System Info */}
          <div className="loading__info">
            <div className="loading__logo">
              <div className="loading__logo-icon">C</div>
              <span className="loading__logo-text">CYPHER</span>
            </div>
            
            <div className="loading__messages">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentMessage}
                  className="loading__message"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {loadingMessages[currentMessage]}
                </motion.div>
              </AnimatePresence>
              
              <div className="loading__submessage">
                {subMessage}
              </div>
            </div>

            {/* Progress Bar */}
            {showProgress && (
              <div className="loading__progress-container">
                <div className="loading__progress-bar">
                  <motion.div 
                    className="loading__progress-fill"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.2 }}
                  />
                  <div className="loading__progress-glow"></div>
                </div>
                <div className="loading__progress-text">
                  {Math.round(progress)}%
                </div>
              </div>
            )}

            {/* System Status */}
            <div className="loading__status">
              <div className="loading__status-grid">
                <div className="loading__status-item">
                  <span className="loading__status-label">NETWORK</span>
                  <span className="loading__status-value">ONLINE</span>
                </div>
                <div className="loading__status-item">
                  <span className="loading__status-label">SECURITY</span>
                  <span className="loading__status-value">ACTIVE</span>
                </div>
                <div className="loading__status-item">
                  <span className="loading__status-label">AUDIO</span>
                  <span className="loading__status-value">READY</span>
                </div>
                <div className="loading__status-item">
                  <span className="loading__status-label">MODE</span>
                  <span className="loading__status-value">TACTICAL</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom UI Elements */}
        <div className="loading__footer">
          <div className="loading__scan-line"></div>
          <div className="loading__footer-text">
            CYPHER AUDIO SYSTEMS © 2024 | MECHA INTERFACE v2.1
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Loading;